import { Link } from 'react-router-dom';

import { XoneraMark } from '../common/XoneraMark';

/**
 * The Xonera bar: glass shell + logo lockup, and nothing else.
 *
 * Extracted because there were FOUR copies of it — `Navbar`, `AuthPage` (twice,
 * once per state branch, inside the same file), and `OnboardingWizard` — plus a
 * fifth, differently-shaped lockup on `AccountRestorePage`. They were
 * pixel-identical in light and had drifted three ways in dark:
 *
 *   Navbar             dark:border-white/[0.07]  dark:bg-card/25
 *   AuthPage           dark:border-transparent   dark:bg-transparent
 *   OnboardingWizard   dark:border-white/[0.06]  dark:bg-transparent
 *
 * This file takes the Navbar's values, because those are the ones with a
 * recorded reason: the bar used to go fully transparent in dark and dissolved
 * into the page, so a faint glass fill was added to let it read as a surface
 * floating above the lit room. AuthPage never got that fix — its bar was
 * *entirely invisible* in dark mode, border included. OnboardingWizard's
 * `0.06` vs `0.07` was a typo, not a decision.
 *
 * NOT a `Navbar` variant. `Navbar` calls `useAuth()` and `useUserProfile()`
 * unconditionally at the top level, so a `variant='brand-only'` prop would fire
 * both hooks on auth pages that have no user and no session. Composition keeps
 * the data-fetching in the component that needs data.
 */
interface BrandBarProps {
  /**
   * `fixed` overlays the page and needs `pt-16` on the content below it (the
   * app shell and auth pages). `static` is an in-flow flex child, which is what
   * the onboarding wizard's column needs.
   */
  position?: 'fixed' | 'static';
  /**
   * Link the lockup to `/`. Off by default because the auth and onboarding
   * surfaces have nowhere to send an unauthenticated user — there the brand is
   * decoration, not navigation.
   */
  linkHome?: boolean;
  /** Everything to the right of the brand — what makes a BrandBar a Navbar. */
  children?: React.ReactNode;
}

export function BrandBar({
  position = 'fixed',
  linkHome = false,
  children,
}: BrandBarProps) {
  const lockup = (
    <>
      <XoneraMark className='mr-3 h-8 w-8' />
      <span className='text-xl font-bold text-foreground'>Xonera</span>
    </>
  );

  return (
    <nav
      // Named because it is not the only `nav` landmark on every page -
      // `SettingsNav` renders a second one, and two unlabelled landmarks of
      // the same role cannot be told apart when navigating by landmark.
      aria-label='Main'
      className={
        position === 'fixed'
          ? 'fixed left-0 right-0 top-0 z-50'
          : 'w-full shrink-0'
      }
    >
      {/* Frosted glass. It deliberately draws no lattice of its own — the page
          grid is `fixed`, so it shows through this translucent bar. An earlier
          version painted its own unmasked 50px grid here, which is why the
          lattice used to read brightest INSIDE the navbar. */}
      <div className='relative border-b border-white/20 bg-white/10 backdrop-blur-md dark:border-white/[0.07] dark:bg-card/25'>
        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <div className='flex h-16 items-center justify-between'>
            {linkHome ? (
              <Link
                to='/'
                // `tap-target`: the lockup is 32px tall, tapped as 44 on a phone.
                className='tap-target flex items-center'
              >
                {lockup}
              </Link>
            ) : (
              <div className='flex items-center'>{lockup}</div>
            )}
            {children}
          </div>
        </div>
      </div>
    </nav>
  );
}
